// Q3/Q4 — PWA: đăng ký service worker (`public/sw.js`) và Web Push không chứa nội dung.
import type { SwChatClient } from '@swchat/client-sdk';

const SW_URL = '/sw.js';
/** Chờ service worker sẵn sàng tối đa chừng này (đăng ký lỗi thì `ready` không bao giờ xong). */
const READY_TIMEOUT_MS = 10_000;

interface TrustedTypePolicyLike {
  createScriptURL(url: string): unknown;
}
interface TrustedTypesLike {
  createPolicy(name: string, rules: { createScriptURL: (url: string) => string }): TrustedTypePolicyLike;
}

/**
 * Đăng ký service worker cho web chính (không gọi ở `/embed`). Chỉ bản build (dev server của Vite có HMR, không cần
 * cache). CSP có `require-trusted-types-for 'script'` → `register()` cần TrustedScriptURL: policy chỉ cho đúng `/sw.js`.
 */
export function registerServiceWorker() {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const tt = (window as unknown as { trustedTypes?: TrustedTypesLike }).trustedTypes;
  let url: unknown = SW_URL;
  if (tt) {
    const policy = tt.createPolicy('swchat-sw', {
      createScriptURL: (u) => {
        if (u !== SW_URL) throw new TypeError(`Không cho phép script URL ${u}`);
        return u;
      },
    });
    url = policy.createScriptURL(SW_URL);
  }
  navigator.serviceWorker.register(url as string, { scope: '/' }).catch((e: unknown) => {
    console.warn('Không đăng ký được service worker', e);
  });
}

/** Trình duyệt có đủ API cho Web Push (service worker + PushManager + Notification)? */
export function pushSupported(): boolean {
  return import.meta.env.PROD && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export type PushResult = 'enabled' | 'unsupported' | 'no_permission' | 'server_disabled';

function b64urlToBytes(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

function sameBytes(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a || a.byteLength !== b.length) return false;
  const x = new Uint8Array(a);
  return x.every((v, i) => v === b[i]);
}

async function readyRegistration(): Promise<ServiceWorkerRegistration> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) => (timer = setTimeout(() => reject(new Error('Service worker chưa sẵn sàng')), READY_TIMEOUT_MS))),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Đăng ký Web Push cho thiết bị này (cần quyền Notification đã `granted`): lấy khóa VAPID của server →
 * `pushManager.subscribe` (dùng lại subscription cũ nếu cùng khóa) → gửi lên server. Gọi lại an toàn (đồng bộ lại
 * subscription mỗi lần mở app, ví dụ sau khi trình duyệt xoay subscription).
 */
export async function enableWebPush(client: SwChatClient): Promise<PushResult> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission !== 'granted') return 'no_permission';
  const key = await client.pushPublicKey();
  if (!key) return 'server_disabled';
  const serverKey = b64urlToBytes(key);
  const reg = await readyRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameBytes(sub.options.applicationServerKey, serverKey)) {
    // Server đổi khóa VAPID → subscription cũ không dùng được nữa.
    await sub.unsubscribe().catch(() => undefined);
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: serverKey });
  await client.enablePush(sub.toJSON());
  return 'enabled';
}

/**
 * Thông báo khi tab ẩn (A7) — dự phòng cho trình duyệt không cho `new Notification()` (Chrome Android, PWA đã cài):
 * hiện qua service worker (backlog #8). Bấm vào → service worker mở đúng hội thoại.
 */
export async function showViaServiceWorker(title: string, options: { body: string; tag: string; conversationId: string }) {
  if (!('serviceWorker' in navigator)) return;
  const reg = await readyRegistration();
  await reg.showNotification(title, {
    body: options.body,
    tag: options.tag,
    icon: '/icons/icon-192.png',
    data: { conversation_id: options.conversationId },
  });
}

/** `?c=<id>` khi service worker mở cửa sổ mới từ thông báo; bỏ khỏi URL sau khi đọc. */
export function takeConversationParam(): string | undefined {
  const params = new URLSearchParams(window.location.search);
  const id = params.get('c');
  if (!id) return undefined;
  params.delete('c');
  const rest = params.toString();
  history.replaceState(history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
  return /^[0-9a-f-]{36}$/i.test(id) ? id : undefined;
}

/** Service worker báo "mở hội thoại" (bấm thông báo khi app đang mở). Trả hàm hủy đăng ký. */
export function onOpenConversation(handler: (conversationId: string) => void): () => void {
  if (!('serviceWorker' in navigator)) return () => {};
  const listener = (e: MessageEvent) => {
    const d = e.data as { type?: unknown; conversation_id?: unknown } | undefined;
    if (d?.type === 'open_conversation' && typeof d.conversation_id === 'string') handler(d.conversation_id);
  };
  navigator.serviceWorker.addEventListener('message', listener);
  return () => navigator.serviceWorker.removeEventListener('message', listener);
}
